import React from 'react';
import styled from 'styled-components';
import {
  FileText,
  Search,
  Users,
  Map,
  BookOpen,
  MessageCircle,
  ChevronRight,
  HeadphonesIcon
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSiteContent } from '../hooks/useSiteContent';

const Container = styled.section`
  background: linear-gradient(180deg, var(--background-light) 0%, var(--white) 100%);
  padding: 5rem 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, var(--border-light), transparent);
  }
`;

const Content = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3.5rem;
`;

const Subtitle = styled.span`
  display: inline-block;
  color: var(--secondary-red);
  font-weight: 700;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: 0.75rem;
`;

const Title = styled.h2`
  font-family: var(--font-secondary);
  font-size: 2.5rem;
  color: var(--text-dark);
  margin-bottom: 1rem;
  font-weight: 800;

  @media (max-width: 768px) {
    font-size: 1.875rem;
  }
`;

const Description = styled.p`
  color: var(--text-light);
  font-size: 1.1rem;
  max-width: 600px;
  margin: 0 auto;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const AccessCard = styled(Link)`
  background: var(--white);
  padding: 2rem;
  border-radius: var(--radius-lg);
  transition: var(--transition);
  border: 1px solid var(--border-light);
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${props => props.$gradient};
    opacity: 0;
    transition: var(--transition);
    z-index: 0;
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: var(--shadow-lg);
    border-color: transparent;

    &::before {
      opacity: 1;
    }

    .card-icon {
      background: var(--white);
      color: ${props => props.$iconColor};
      transform: scale(1.1) rotate(5deg);
    }

    .card-title,
    .card-description,
    .card-link {
      color: var(--white);
      position: relative;
      z-index: 1;
    }

    .card-link svg {
      transform: translateX(4px);
    }
  }
`;

const IconWrapper = styled.div`
  width: 56px;
  height: 56px;
  background: ${props => props.$bg};
  color: var(--white);
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: var(--transition);
  position: relative;
  z-index: 1;
`;

const CardContent = styled.div`
  flex: 1;
`;

const CardTitle = styled.h3`
  font-size: 1.15rem;
  color: var(--text-dark);
  margin-bottom: 0.5rem;
  font-weight: 700;
  transition: var(--transition);
`;

const CardDescription = styled.p`
  font-size: 0.9rem;
  color: var(--text-light);
  line-height: 1.6;
  transition: var(--transition);
`;

const CardLink = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--primary-blue);
  font-weight: 600;
  font-size: 0.9rem;
  transition: var(--transition);
  margin-top: auto;

  svg {
    transition: var(--transition);
  }
`;

const ICON_MAP = {
  FileText,
  Search,
  Users,
  Map,
  BookOpen,
  MessageCircle,
  HeadphonesIcon
};

const CARD_THEMES = [
  {
    bg: 'linear-gradient(135deg, #C8102E, #e52e4e)',
    gradient: 'linear-gradient(135deg, rgba(200, 16, 46, 0.95), rgba(229, 46, 78, 0.95))',
    iconColor: '#C8102E'
  },
  {
    bg: 'linear-gradient(135deg, #002664, #004d8f)',
    gradient: 'linear-gradient(135deg, rgba(0, 38, 100, 0.95), rgba(0, 77, 143, 0.95))',
    iconColor: '#002664'
  },
  {
    bg: 'linear-gradient(135deg, #009639, #00b347)',
    gradient: 'linear-gradient(135deg, rgba(0, 150, 57, 0.95), rgba(0, 179, 71, 0.95))',
    iconColor: '#009639'
  },
  {
    bg: 'linear-gradient(135deg, #7c3aed, #8b5cf6)',
    gradient: 'linear-gradient(135deg, rgba(124, 58, 237, 0.95), rgba(139, 92, 246, 0.95))',
    iconColor: '#7c3aed'
  },
  {
    bg: 'linear-gradient(135deg, #ea580c, #f97316)',
    gradient: 'linear-gradient(135deg, rgba(234, 88, 12, 0.95), rgba(249, 115, 22, 0.95))',
    iconColor: '#ea580c'
  },
  {
    bg: 'linear-gradient(135deg, #0891b2, #06b6d4)',
    gradient: 'linear-gradient(135deg, rgba(8, 145, 178, 0.95), rgba(6, 182, 212, 0.95))',
    iconColor: '#0891b2'
  }
];

const QuickAccess = () => {
  const { content } = useSiteContent('home_quick_access');
  const section = content || {};
  const items = Array.isArray(section.items) ? section.items : [];

  return (
    <Container>
      <Content>
        <SectionHeader>
          <Subtitle>{section.subtitle || 'Nos Services'}</Subtitle>
          <Title>{section.title || 'Accès rapides'}</Title>
          <Description>
            {section.description || 'Tous nos services sont gratuits, confidentiels et accessibles à tous les citoyens tchadiens.'}
          </Description>
        </SectionHeader>

        <Grid>
          {items.map((service, index) => {
            const Icon = ICON_MAP[service.icon] || FileText;
            const theme = CARD_THEMES[index % CARD_THEMES.length];
            const cardTheme = {
              bg: service.bg || theme.bg,
              gradient: service.gradient || theme.gradient,
              iconColor: service.iconColor || theme.iconColor
            };

            return (
              <AccessCard
                key={`${service.title || 'item'}-${index}`}
                to={service.link || '#'}
                $gradient={cardTheme.gradient}
                $iconColor={cardTheme.iconColor}
              >
                <IconWrapper $bg={cardTheme.bg} className="card-icon">
                  <Icon size={24} />
                </IconWrapper>

                <CardContent>
                  <CardTitle className="card-title">{service.title || ''}</CardTitle>
                  <CardDescription className="card-description">
                    {service.description || ''}
                  </CardDescription>
                </CardContent>

                <CardLink className="card-link">
                  {section.ctaLabel || 'Accéder'} <ChevronRight size={16} />
                </CardLink>
              </AccessCard>
            );
          })}
        </Grid>
      </Content>
    </Container>
  );
};

export default QuickAccess;
